import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, User, ExternalLink, FileText, Phone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cases } from "@/data/cases";
import { casesTranslations } from "@/data/casesTranslations";
import { useLanguage } from "@/contexts/LanguageContext";
import projectAI from "@/assets/project-ai.jpg";

const Projects = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const isEN = language === "en";
  
  const translatedCases = casesTranslations[language].cases.map((translatedCase, index) => ({
    ...cases[index],
    ...translatedCase,
    details: {
      ...cases[index].details,
      ...translatedCase.details,
      figmaLink: cases[index].details.figmaLink,
      prototypeLink: cases[index].details.prototypeLink,
      images: cases[index].details.images.map((img, i) => ({
        ...img,
        caption: translatedCase.details.images[i].caption
      }))
    }
  }));

  const legacyProjects = [
    {
      title: t("projects.legacy.title"),
      image: projectAI,
      company: t("projects.legacy.company"),
      role: t("projects.legacy.role"),
      description: t("projects.legacy.description"),
      tags: ["GPT-4", "OpenAI", "Typebot"],
    },
  ];

  return (
    <section id="projects" className="py-24 bg-secondary/30">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{t("projects.title")}</h2>
          <p className="text-muted-foreground text-lg">{t("projects.subtitle")}</p>
          <div className="w-20 h-1 bg-primary mx-auto mt-6" />
        </div>

        {/* Featured Cases */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold mb-6">{t("projects.detailedCases")}</h3>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Voicebots Odonto — Project Case */}
            <Card
              className="overflow-hidden gradient-premium border-primary/20 hover:border-primary/60 transition-all hover:shadow-glow hover:-translate-y-2 animate-fade-in group cursor-pointer"
              style={{ animationDelay: "0s" }}
              onClick={() => navigate("/case-voicebots-odonto")}
            >
              <div className="relative h-64 overflow-hidden bg-gradient-to-br from-slate-900 to-slate-700">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/30 flex items-center justify-center mx-auto mb-3">
                      <Phone className="h-7 w-7 text-primary-foreground" />
                    </div>
                    <p className="text-slate-200 text-sm font-medium">
                      {isEN ? "Voicebot Ecosystem · GPT-4o + n8n" : "Ecossistema de Voicebots · GPT-4o + n8n"}
                    </p>
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                <div className="absolute top-4 right-4">
                  <div className="w-10 h-10 bg-primary/90 rounded-full flex items-center justify-center">
                    <ExternalLink className="h-5 w-5 text-white" />
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  {isEN
                    ? "Voicebot Ecosystem for a Dental Clinic"
                    : "Ecossistema de Voicebots para Clínica Odontológica"}
                </h3>
                
                <div className="flex flex-wrap gap-3 mb-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Building2 className="h-4 w-4 text-primary" />
                    {isEN ? "Dental Clinic" : "Clínica Odontológica"}
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4 text-primary" />
                    {isEN
                      ? "UX Writer, Conversational Designer & Prompt Engineer"
                      : "UX Writer, Conversational Designer & Prompt Engineer"}
                  </div>
                </div>
                
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {isEN
                    ? "Modular VUI architecture and three AI personas for follow-up, friendly collections and reactivation — designed for the 50+ audience."
                    : "Arquitetura VUI modular e três personas de IA para follow-up, cobrança amigável e reativação — desenhadas para o público 50+."}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {["VUI", "Conversation Design", "Prompt Engineering", "GPT-4o", "n8n"].map((tag, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>

                <Button
                  className="w-full mt-4 gradient-accent shadow-accent"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/case-voicebots-odonto");
                  }}
                >
                  {t("projects.viewFullCase")}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </Card>

            {translatedCases.map((project, index) => (
              <Card
                key={index}
                className="overflow-hidden gradient-premium border-primary/20 hover:border-primary/60 transition-all hover:shadow-glow hover:-translate-y-2 animate-fade-in group cursor-pointer"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(`/case/${project.slug}`)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                  <div className="absolute top-4 right-4">
                    <div className="w-10 h-10 bg-primary/90 rounded-full flex items-center justify-center">
                      <ExternalLink className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                    {project.title}
                  </h3>
                  
                  <div className="flex flex-wrap gap-3 mb-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Building2 className="h-4 w-4 text-primary" />
                      {project.company}
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4 text-primary" />
                      {project.role}
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, i) => (
                      <Badge
                        key={i}
                        variant="secondary"
                        className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  <Button
                    className="w-full mt-4 gradient-accent shadow-accent"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/case/${project.slug}`);
                    }}
                  >
                    {t("projects.viewFullCase")}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Case Studies */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold mb-6">{isEN ? "Case Studies" : "Estudos de Caso"}</h3>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Crush Virtual */}
            <Card
              className="overflow-hidden gradient-premium border-primary/40 hover:border-primary transition-all hover:shadow-glow hover:-translate-y-2 animate-fade-in group cursor-pointer relative"
              onClick={() => navigate("/artigo-crush-virtual")}
            >
              <div className="absolute top-4 left-4 z-10">
                <Badge className="bg-primary text-primary-foreground font-semibold px-3 py-1 text-xs uppercase tracking-wider">
                  {isEN ? "Case Study" : "Estudo de Caso"}
                </Badge>
              </div>
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://miro.medium.com/v2/resize:fit:700/1*DOLwsdSETz8ZJzzH9eoMgw.png"
                  alt="Crush Virtual - IA e Dark UX"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                  {isEN
                    ? "When your virtual crush is a robot: AI, Dark UX and fake profiles"
                    : "Quando seu crush virtual é um robô: IA, Dark UX e perfis falsos"}
                </h3>
                <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
                  {isEN
                    ? "A real investigation into how chatbots and AI are being used for deceptive automation and financial scams."
                    : "Uma investigação real sobre como chatbots e IA estão sendo usados para automação enganosa e golpes financeiros."}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["Dark UX", "IA Generativa", "Conversation Design"].map((tag, i) => (
                    <Badge key={i} variant="secondary" className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <Button
                  className="w-full gradient-accent shadow-accent"
                  onClick={(e) => { e.stopPropagation(); navigate("/artigo-crush-virtual"); }}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  {isEN ? "Read Case Study" : "Ler Estudo de Caso"}
                </Button>
              </div>
            </Card>

            {/* Vozis */}
            <Card
              className="overflow-hidden gradient-premium border-primary/40 hover:border-primary transition-all hover:shadow-glow hover:-translate-y-2 animate-fade-in group cursor-pointer relative"
              onClick={() => navigate("/case-vozis")}
            >
              <div className="absolute top-4 left-4 z-10">
                <Badge className="bg-primary text-primary-foreground font-semibold px-3 py-1 text-xs uppercase tracking-wider">
                  {isEN ? "Case Study" : "Estudo de Caso"}
                </Badge>
              </div>
              <div className="relative h-48 overflow-hidden bg-emerald-950 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-emerald-700 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3">V</div>
                  <p className="text-emerald-300 text-sm font-medium">Vozis AI</p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                  {isEN
                    ? "Vozis — AI Voice Prospecting at Scale"
                    : "Vozis — Prospecção por IA de Voz em Escala"}
                </h3>
                <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
                  {isEN
                    ? "An AI platform that automates sales calls with human-like naturalness, multiplying qualified contacts without growing the team."
                    : "Uma plataforma de IA que automatiza ligações comerciais com naturalidade humana, multiplicando contatos qualificados sem aumentar equipe."}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {[isEN ? "Voice AI" : "IA de Voz", "n8n", "Sales Automation", "CRM"].map((tag, i) => (
                    <Badge key={i} variant="secondary" className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <Button
                  className="w-full gradient-accent shadow-accent"
                  onClick={(e) => { e.stopPropagation(); navigate("/case-vozis"); }}
                >
                  <Phone className="mr-2 h-4 w-4" />
                  {isEN ? "View Interactive Case" : "Ver Case Interativo"}
                </Button>
              </div>
            </Card>

          </div>
        </div>

        {/* Legacy Projects */}
        <div>
          <h3 className="text-2xl font-bold mb-6">{t("projects.otherProjects")}</h3>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {legacyProjects.map((project, index) => (
            <Card
              key={index}
              className="overflow-hidden gradient-premium border-primary/20 hover:border-primary/40 transition-all hover:shadow-accent hover:-translate-y-2 animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
              </div>
              
              <div className="p-6">
                <h3 className="text-2xl font-bold mb-3">{project.title}</h3>
                
                <div className="flex flex-wrap gap-3 mb-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Building2 className="h-4 w-4 text-primary" />
                    {project.company}
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4 text-primary" />
                    {project.role}
                  </div>
                </div>
                
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  {project.description}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
